import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../../theme/chart';
import { tokens } from '../../../theme/tokens';
import { parseLocalDate } from '../../../utils/bills';
import type { Goal } from './dashboard.types';
import { Meter, Panel, PanelHeader, Quiet } from './ui';

/** "Metas": each goal's bar, what is saved of what, and the deadline. */
export function GoalsProgressCard({ goals, onSeeAll }: { goals: Goal[]; onSeeAll?: () => void }) {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language;

  return (
    <Panel label={t('home.goals.title')} sx={{ gap: 2 }}>
      <PanelHeader title={t('home.goals.title')} action={onSeeAll ? { label: t('home.seeGoals'), onClick: onSeeAll } : undefined} />
      {goals.length === 0 && <Quiet>{t('home.noGoals')}</Quiet>}
      {goals.map((goal) => {
        const done = goal.progress >= 1;
        const pct = Math.round(Math.min(goal.progress, 1) * 100);
        const saved = formatMoney(goal.saved, goal.currency, locale);
        const target = formatMoney(goal.target, goal.currency, locale);
        const amounts = goal.target > 0
          ? t(goal.habit ? 'home.goals.thisMonth' : 'home.goals.ofTarget', { saved, target })
          : saved;
        const deadline = !goal.habit && goal.deadline
          ? t('home.goals.until', { date: parseLocalDate(goal.deadline).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) })
          : null;
        return (
          <Box key={goal.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box component='span' sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{goal.name}</Box>
              <Box component='span' sx={{ fontSize: 13, color: done ? tokens.accent : tokens.ink3, fontWeight: done ? 700 : 400 }}>
                {done ? t('home.goalDone') : `${pct}%`}
              </Box>
            </Box>
            <Meter ratio={goal.progress} track={tokens.line} />
            <Box component='span' sx={{ fontSize: 13, color: tokens.ink2 }}>
              {amounts}
              {deadline && ` · ${deadline}`}
            </Box>
          </Box>
        );
      })}
    </Panel>
  );
}
